import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBag, Trash2, Plus, Minus, QrCode, Upload, Check, Loader, ArrowLeft, MapPin
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL, resolveImageUrl } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Cart.css';



const Cart = () => {
  const { 
    cartItems, 
    cartCount, 
    cartTotal, 
    wishlistCount,
    user,
    token,
    handleUpdateCartQuantity, 
    handleRemoveFromCart, 
    handleClearCart 
  } = useCart();

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customerName: prev.customerName || user.name || '',
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    address: '',
    landmark: '',
    pincode: '',
    coordinates: '',
    message: '',
    paymentScreenshot: ''
  });
  
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  
  const [paymentSettings, setPaymentSettings] = useState({
    upiId: 'himalayannuts@upi',
    qrCode: ''
  });

  useEffect(() => {
    fetch(`${API_URL}/settings/payment_settings`)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to fetch payment settings');
      })
      .then(data => {
        if (data && data.upiId) {
          setPaymentSettings(data);
        }
      })
      .catch(err => {
        console.error('Error loading payment settings:', err);
      });
  }, []);

  const handleFetchLiveLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`, {
            headers: {
              'Accept-Language': 'en'
            }
          });
          if (!res.ok) throw new Error('Failed to fetch address details');
          const data = await res.json();
          
          if (data && data.display_name) {
            const addressDetails = data.address || {};
            const postcode = addressDetails.postcode || '';
            const landmarkVal = addressDetails.suburb || addressDetails.neighbourhood || addressDetails.road || '';
            
            setFormData(prev => ({
              ...prev,
              address: data.display_name,
              pincode: postcode || prev.pincode,
              landmark: landmarkVal || prev.landmark,
              coordinates: `Lat: ${latitude.toFixed(6)}, Lon: ${longitude.toFixed(6)}`
            }));
            alert('Live location loaded successfully!');
          }
        } catch (err) {
          console.error(err);
          setFormData(prev => ({
            ...prev,
            address: prev.address,
            coordinates: `Lat: ${latitude.toFixed(6)}, Lon: ${longitude.toFixed(6)}`
          }));
          alert('Failed to get address details. GPS coordinates captured instead.');
        } finally {
          setFetchingLocation(false);
        }
      },
      (error) => {
        console.error(error);
        alert('Permission denied or error getting location: ' + error.message);
        setFetchingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Calculations
  const deliveryFee = cartTotal >= 499 ? 0 : 40;
  const finalTotal = cartTotal + deliveryFee;

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const bodyFormData = new FormData();
    bodyFormData.append('screenshot', file);

    try {
      const res = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        body: bodyFormData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload receipt.');
      
      setFormData(prev => ({ ...prev, paymentScreenshot: data.url }));
      alert('Payment receipt uploaded successfully!');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error occurred while uploading payment screenshot.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.phone || !formData.address) {
      alert('Please fill in your Name, Phone Number, and Delivery Address.');
      return;
    }

    if (formData.phone.replace(/\D/g, '').length !== 10) {
      alert('Please enter a valid 10-digit Mobile Number.');
      return;
    }

    setSubmitting(true);
    const orderSummary = cartItems.map(item => ({
      productId: item.product.id,
      title: item.product.title,
      price: item.product.price,
      quantity: item.quantity
    }));

    if (!formData.pincode) {
      alert('Pincode is required.');
      setSubmitting(false);
      return;
    }

    if (!formData.paymentScreenshot) {
      alert('Please upload your payment receipt screenshot to place the order.');
      setSubmitting(false);
      return;
    }

    const gpsPart = formData.coordinates ? ` (GPS: ${formData.coordinates})` : '';
    const fullAddress = `${formData.address} [Landmark: ${formData.landmark || 'N/A'}, Pincode: ${formData.pincode}]${gpsPart}`;
    const payload = {
      customerName: formData.customerName,
      address: fullAddress,
      phone: formData.phone,
      message: formData.message || null,
      orderSummary: orderSummary,
      totalAmount: finalTotal,
      paymentScreenshot: formData.paymentScreenshot
    };

    try {
      const res = await fetch(`${API_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to place order.');

      setSubmittedOrder(data.order);
      handleClearCart(); // Empty client-side cart
      setFormData({
        customerName: '',
        phone: '',
        address: '',
        landmark: '',
        pincode: '',
        coordinates: '',
        message: '',
        paymentScreenshot: ''
      });
    } catch (err) {
      console.error(err);
      alert(err.message || 'An error occurred while placing your order.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cart-page-wrapper">
      <TopBar />
      <Header wishlistCount={wishlistCount} cartCount={cartCount} />
      <Navbar activeItem="" />

      <main className="cart-main-container container">
        {submittedOrder ? (
          <div className="cart-success-card">
            <div className="success-icon-badge">
              <Check size={36} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--dark-green)', margin: '0 0 10px 0' }}>
              Order Placed Successfully!
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--gray)', marginBottom: '20px' }}>
              Thank you for shopping with Himalayan Nuts. Your order has been registered and is under verification.
            </p>
            <div className="success-order-id" style={{ display: 'inline-block', fontSize: '14px' }}>
              Order Reference ID: <strong>{submittedOrder.id}</strong>
            </div>

            <div className="summary-card" style={{ maxWidth: '450px', margin: '24px auto', textAlign: 'left' }}>
              <h4 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '10px', color: 'var(--dark-text)' }}>Delivery Summary</h4>
              <div style={{ fontSize: '13px', color: 'var(--dark-text)', lineHeight: '1.5' }}>
                <div><strong>Name:</strong> {submittedOrder.customerName}</div>
                <div><strong>Phone:</strong> {submittedOrder.phone}</div>
                <div><strong>Address:</strong> {submittedOrder.address}</div>
                {submittedOrder.message && <div><strong>Instructions:</strong> {submittedOrder.message}</div>}
                <div style={{ marginTop: '10px', borderTop: '1px solid #ddd', paddingTop: '8px', fontSize: '14px', fontWeight: 700 }}>
                  <strong>Amount Billed:</strong> ₹{submittedOrder.totalAmount}
                </div>
              </div>
            </div>

            <Link to="/">
              <button className="btn-shopping-return">
                <span>Continue Shopping</span>
              </button>
            </Link>
          </div>
        ) : cartItems.length === 0 ? (
          <div className="cart-empty-state">
            <div className="empty-state-icon-box">
              <ShoppingBag size={48} />
            </div>
            <h2>Your Shopping Cart is Empty</h2>
            <p>Looks like you haven't added any products to your cart yet.</p>
            <Link to="/">
              <button className="btn-shopping-return">
                <span>Explore Products</span>
              </button>
            </Link>
          </div>
        ) : (
          <>
            <div className="cart-title-row">
              <ShoppingBag size={28} style={{ color: 'var(--dark-green)' }} />
              <h1>Shopping Cart ({cartCount} {cartCount === 1 ? 'item' : 'items'})</h1>
            </div>

            <div className="cart-content-grid">
              {/* Left Side: Cart Items */}
              <div className="cart-items-panel">
                <div className="cart-table-header">
                  <span>Product Details</span>
                  <span>Price</span>
                  <span>Quantity</span>
                  <span style={{ textAlign: 'right' }}>Total</span>
                </div>

                {cartItems.map((item) => (
                  <div key={item.product.id} className="cart-table-row">
                    {/* Info */}
                    <div className="cart-product-cell">
                      <img 
                        src={resolveImageUrl(item.product.image)} 
                        alt={item.product.title} 
                        className="cart-product-img" 
                      />
                      <div>
                        <h4 className="cart-product-title" style={{ margin: 0 }}>{item.product.title}</h4>
                      </div>
                    </div>

                    {/* Unit Price */}
                    <div className="cart-price-cell">
                      ₹{item.product.price}
                    </div>

                    {/* Qty Selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="qty-selector">
                        <button 
                          className="qty-btn" 
                          onClick={() => handleUpdateCartQuantity(item.product.id, item.quantity - 1)}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="qty-num">{item.quantity}</span>
                        <button 
                          className="qty-btn" 
                          onClick={() => handleUpdateCartQuantity(item.product.id, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      
                      <button 
                        className="remove-item-btn" 
                        onClick={() => handleRemoveFromCart(item.product.id)}
                        aria-label="Remove item"
                        style={{ marginLeft: '4px' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Subtotal */}
                    <div className="cart-subtotal-cell" style={{ textAlign: 'right' }}>
                      ₹{item.product.price * item.quantity}
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-start' }}>
                  <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, color: 'var(--primary-green)' }}>
                    <ArrowLeft size={16} />
                    <span>Continue Shopping</span>
                  </Link>
                </div>
              </div>

              {/* Right Side: Summary & Checkout */}
              <div className="checkout-summary-panel">
                <h3 className="checkout-panel-title">Order Summary</h3>
                
                <div className="summary-card" style={{ background: '#fafafa', border: '1px solid #eee', padding: '16px', borderRadius: '12px' }}>
                  <div className="summary-row">
                    <span>Items Subtotal</span>
                    <span>₹{cartTotal}</span>
                  </div>
                  <div className="summary-row">
                    <span>Delivery Charges</span>
                    <span>{deliveryFee === 0 ? 'FREE' : `₹${deliveryFee}`}</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div style={{ fontSize: '12px', color: 'var(--primary-green)', marginTop: '-4px', marginBottom: '10px' }}>
                      Add ₹{499 - cartTotal} more for FREE delivery!
                    </div>
                  )}
                  <div className="summary-row total" style={{ borderTop: '1px solid #ddd', paddingTop: '12px', marginTop: '12px', fontWeight: 700, fontSize: '18px' }}>
                    <span>Total Amount</span>
                    <span style={{ color: 'var(--dark-green)' }}>₹{finalTotal}</span>
                  </div>
                </div>

                {/* Checkout Details Form */}
                <h3 className="checkout-panel-title" style={{ marginTop: '30px' }}>Delivery Details</h3>
                
                <form onSubmit={handleSubmitOrder}>
                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label className="form-label">Full Name *</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={formData.customerName}
                      onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                      placeholder="e.g. John Doe"
                      required
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label className="form-label">Mobile Number *</label>
                     <input 
                      type="tel" 
                      className="form-input" 
                      value={formData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 10) {
                          setFormData(prev => ({ ...prev, phone: val }));
                        }
                      }}
                      placeholder="e.g. 9876543210"
                      required
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label className="form-label">Complete Shipping Address *</label>
                    <textarea 
                      className="form-input" 
                      style={{ height: '80px', resize: 'none', padding: '10px' }}
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="House No, Street, Locality"
                      required
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div className="form-group">
                      <label className="form-label">Pincode *</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="pincode"
                        value={formData.pincode}
                        onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                        placeholder="e.g. 110001"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Landmark</label>
                      <input 
                        type="text" 
                        className="form-input"
                        name="landmark"
                        value={formData.landmark}
                        onChange={(e) => setFormData(prev => ({ ...prev, landmark: e.target.value }))}
                        placeholder="e.g. Near Post Office"
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label className="form-label">GPS Coordinates (Optional)</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text" 
                        className="form-input"
                        name="coordinates"
                        value={formData.coordinates}
                        onChange={(e) => setFormData(prev => ({ ...prev, coordinates: e.target.value }))}
                        placeholder="e.g. Lat: 28.5355, Lon: 77.3910"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={handleFetchLiveLocation}
                        disabled={fetchingLocation}
                        style={{
                          background: 'var(--primary-green)',
                          color: '#ffffff',
                          border: 'none',
                          padding: '0 16px',
                          borderRadius: '25px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {fetchingLocation ? (
                          <>
                            <Loader size={14} className="spin-animation" style={{ color: '#ffffff' }} />
                            <span>Fetching...</span>
                          </>
                        ) : (
                          <>
                            <MapPin size={14} />
                            <span>Get Live Location</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label className="form-label">Order Note (Optional)</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Instructions for delivery..."
                    />
                  </div>

                  {/* Payment Details */}
                  <div className="cart-upi-box">
                    <div className="cart-upi-title">
                      <QrCode size={18} />
                      <span>Instant UPI Payment</span>
                    </div>
                    <p style={{ fontSize: '12px', margin: '0 0 12px 0', color: 'var(--gray)', lineHeight: '1.4' }}>
                      Please transfer the total amount <strong style={{ color: 'var(--dark-text)' }}>₹{finalTotal}</strong> to UPI ID: <strong style={{ color: 'var(--dark-text)' }}>{paymentSettings.upiId}</strong>.
                    </p>

                    {/* QR Code Scanner display */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', margin: '14px 0', padding: '12px', border: '1px dashed var(--border-color)', borderRadius: '8px', backgroundColor: '#fcfcfc' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Scan to Pay</span>
                      <img 
                        src={
                          paymentSettings.qrCode 
                            ? resolveImageUrl(paymentSettings.qrCode) 
                            : `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
                                `upi://pay?pa=${paymentSettings.upiId}&pn=Himalayan%20Nuts&am=${finalTotal}&cu=INR&tn=Order`
                              )}`
                        } 
                        alt="UPI Payment QR Code Scanner" 
                        style={{ width: '180px', height: '180px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '10px', color: 'var(--gray)', textAlign: 'center' }}>
                        {paymentSettings.qrCode 
                          ? `Scan with GPay, PhonePe, Paytm, or BHIM and pay exactly ₹${finalTotal}.` 
                          : `Scan with any UPI App (GPay, PhonePe, Paytm). Amount ₹${finalTotal} is requested automatically.`
                        }
                      </span>
                    </div>
                    
                    <div className="form-group" style={{ marginTop: '16px' }}>
                      <label className="form-label">Upload Payment Receipt Screenshot *</label>
                      <input 
                        type="file" 
                        accept="image/*"
                        id="page-receipt-upload"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />
                      <label 
                        htmlFor="page-receipt-upload" 
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px',
                          border: formData.paymentScreenshot ? '2px solid var(--primary-green)' : '2px dashed var(--border-color)',
                          borderRadius: '12px',
                          padding: '24px 16px',
                          backgroundColor: formData.paymentScreenshot ? 'rgba(46, 93, 52, 0.03)' : '#fcfcfc',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          textAlign: 'center',
                          marginTop: '6px'
                        }}
                      >
                        {uploading ? (
                          <>
                            <Loader size={28} className="spin-animation" style={{ color: 'var(--primary-green)' }} />
                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark-text)' }}>Uploading receipt screenshot...</span>
                          </>
                        ) : formData.paymentScreenshot ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(46, 93, 52, 0.1)', color: 'var(--primary-green)' }}>
                              <Check size={20} />
                            </div>
                            <div>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--primary-green)', display: 'block' }}>Receipt Uploaded Successfully!</span>
                              <span style={{ fontSize: '11px', color: 'var(--gray)', display: 'block', marginTop: '2px' }}>Click here to replace screenshot.</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.03)', color: 'var(--gray)' }}>
                              <Upload size={20} />
                            </div>
                            <div>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dark-text)', display: 'block' }}>Click to Upload Receipt</span>
                              <span style={{ fontSize: '11px', color: 'var(--gray)', display: 'block', marginTop: '2px' }}>Compulsory field to confirm order</span>
                            </div>
                          </>
                        )}
                      </label>

                      {formData.paymentScreenshot && (
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: 'var(--gray)' }}>Uploaded Receipt Preview:</span>
                          <img 
                            src={resolveImageUrl(formData.paymentScreenshot)} 
                            alt="Receipt Preview" 
                            style={{ maxWidth: '120px', height: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '4px', display: 'inline-block' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="btn-checkout-primary"
                    disabled={submitting || uploading || !formData.paymentScreenshot}
                    style={{ width: '100%', padding: '14px', fontSize: '16px' }}
                  >
                    {submitting ? 'Placing Order...' : `Confirm Order (₹${finalTotal})`}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Cart;
